import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, BRL } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2 } from "lucide-react";

const STATUS = ["rascunho","enviado","visualizado","aprovado","recusado","expirado"];

export default function Orcamentos() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [form, setForm] = useState({ customer_id: "", items: [{ product: "", quantity: 15, unit_price: 0, fabric: "", technique: "" }], discount: 0, validity_days: 15, notes: "" });

  const load = async () => { const [q, c] = await Promise.all([api.get("/quotes"), api.get("/customers")]); setItems(q.data); setCustomers(c.data); };
  useEffect(() => { load(); }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const cid = searchParams.get("customer");
    if (cid) { setForm((f) => ({ ...f, customer_id: cid })); setOpen(true); setSearchParams({}, { replace: true }); }
  }, [searchParams]);

  const addItem = () => setForm({...form, items: [...form.items, { product:"", quantity:15, unit_price:0, fabric:"", technique:"" }]});
  const rmItem = (i) => setForm({...form, items: form.items.filter((_, ix) => ix !== i)});
  const setItem = (i, k, v) => setForm({...form, items: form.items.map((it, ix) => ix === i ? {...it, [k]: v} : it)});

  const create = async () => {
    if (!form.customer_id) return toast.error("Selecione cliente");
    const payload = { ...form, items: form.items.map((i) => ({...i, quantity: Number(i.quantity)||0, unit_price: Number(i.unit_price)||0})), discount: Number(form.discount)||0, validity_days: Number(form.validity_days)||15 };
    await api.post("/quotes", payload); toast.success("Orçamento criado"); setOpen(false); load();
  };
  const setStatus = async (id, status) => { await api.patch(`/quotes/${id}/status`, { status }); load(); };
  const del = async (id) => { await api.delete(`/quotes/${id}`); load(); };
  const openView = async (id) => { const { data } = await api.get(`/quotes/${id}`); setView(data); };

  const subtotal = form.items.reduce((a,b) => a + (Number(b.quantity)||0) * (Number(b.unit_price)||0), 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Orçamentos</h1>
          <p className="text-slate-400 text-sm mt-1">Propostas comerciais para clientes</p>
        </div>
        <Button onClick={()=>setOpen(true)} className="bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E] font-semibold shrink-0" data-testid="quote-new-btn"><Plus size={16} className="sm:mr-2"/><span className="hidden sm:inline">Novo orçamento</span></Button>
      </div>

      <div className="hidden md:block rounded-xl border border-[#1E293B] bg-[#111622] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0B0F17] text-slate-400">
            <tr>{["Número","Cliente","Itens","Total","Status","Ações"].map(h=><th key={h} className="text-left px-4 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="6" className="text-center py-12 text-slate-500">Sem orçamentos.</td></tr>}
            {items.map((q) => {
              const c = customers.find((x)=>x.id===q.customer_id);
              return (
                <tr key={q.id} className="border-t border-slate-800/70" data-testid={`quote-row-${q.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{q.number}</td>
                  <td className="px-4 py-3">{c?.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-300">{q.items?.length||0}</td>
                  <td className="px-4 py-3 text-[#0CDBF2] font-mono">{BRL(q.total)}</td>
                  <td className="px-4 py-3">
                    <select value={q.status} onChange={(e)=>setStatus(q.id, e.target.value)} data-testid={`quote-status-${q.id}`}
                      className="bg-[#07090E] border border-slate-800 rounded-md h-8 px-2 text-xs">
                      {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={()=>openView(q.id)} data-testid={`quote-view-${q.id}`} className="border-slate-700">Ver</Button>
                      <Button size="sm" variant="outline" onClick={()=>del(q.id)} data-testid={`quote-del-${q.id}`} className="border-slate-700"><Trash2 size={14}/></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {items.length === 0 && <div className="text-center py-10 text-slate-500 text-sm">Sem orçamentos.</div>}
        {items.map((q) => { const c = customers.find((x)=>x.id===q.customer_id); return (
          <div key={q.id} data-testid={`quote-card-${q.id}`} className="rounded-xl border border-[#1E293B] bg-[#111622] p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-xs text-slate-400">{q.number}</div>
                <div className="font-medium truncate">{c?.name || "—"}</div>
                <div className="text-xs text-slate-500 mt-1">{q.items?.length||0} item(ns)</div>
              </div>
              <div className="text-[#0CDBF2] font-mono shrink-0">{BRL(q.total)}</div>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <select value={q.status} onChange={(e)=>setStatus(q.id, e.target.value)} data-testid={`quote-status-m-${q.id}`} className="flex-1 bg-[#07090E] border border-slate-800 rounded-md h-9 px-2 text-xs">
                {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <Button size="sm" variant="outline" onClick={()=>openView(q.id)} data-testid={`quote-view-m-${q.id}`} className="border-slate-700">Ver</Button>
              <Button size="sm" variant="outline" onClick={()=>del(q.id)} data-testid={`quote-del-m-${q.id}`} className="border-slate-700"><Trash2 size={14}/></Button>
            </div>
          </div>
        ); })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0B0F17] border-slate-800 text-slate-100 max-w-2xl">
          <DialogHeader><DialogTitle>Novo orçamento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-slate-300 text-sm">Cliente*</Label>
              <select value={form.customer_id} onChange={(e)=>setForm({...form,customer_id:e.target.value})} data-testid="quote-customer"
                className="w-full mt-1.5 bg-[#07090E] border border-slate-800 h-10 px-3 text-sm rounded-md">
                <option value="">Selecione</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <div className="space-y-2">
              <div className="flex justify-between items-center"><div className="text-sm text-slate-300">Itens</div>
                <Button size="sm" variant="outline" onClick={addItem} data-testid="quote-add-item" className="border-slate-700">+ item</Button></div>
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <Input placeholder="Produto" value={it.product} onChange={(e)=>setItem(i,"product",e.target.value)} className="col-span-5 bg-[#07090E] border-slate-800" data-testid={`qi-product-${i}`} />
                  <Input type="number" placeholder="Qtd" value={it.quantity} onChange={(e)=>setItem(i,"quantity",e.target.value)} className="col-span-2 bg-[#07090E] border-slate-800" data-testid={`qi-qty-${i}`} />
                  <Input type="number" placeholder="Preço" value={it.unit_price} onChange={(e)=>setItem(i,"unit_price",e.target.value)} className="col-span-3 bg-[#07090E] border-slate-800" data-testid={`qi-price-${i}`} />
                  <div className="col-span-2 text-xs font-mono text-[#0CDBF2]">{BRL((Number(it.quantity)||0)*(Number(it.unit_price)||0))}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-slate-300 text-sm">Desconto</Label>
                <Input type="number" value={form.discount} onChange={(e)=>setForm({...form,discount:e.target.value})} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="quote-discount"/></div>
              <div><Label className="text-slate-300 text-sm">Validade (dias)</Label>
                <Input type="number" value={form.validity_days} onChange={(e)=>setForm({...form,validity_days:e.target.value})} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="quote-validity"/></div>
              <div className="text-right pt-4"><div className="text-xs text-slate-500">Total</div><div className="font-heading font-bold text-lg text-[#0CDBF2]">{BRL(subtotal - (Number(form.discount)||0))}</div></div>
            </div>
            <Button onClick={create} className="w-full bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E] font-semibold" data-testid="quote-save">Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!view} onOpenChange={(v)=>!v && setView(null)}>
        <DialogContent className="bg-white text-slate-900 max-w-2xl">
          {view && (
            <div className="space-y-4 p-2">
              <div className="flex justify-between border-b pb-3">
                <div><div className="font-heading text-2xl font-extrabold">ATP<span className="text-[#0CDBF2]">.</span>Confecções</div><div className="text-xs text-slate-500">Uniformes personalizados</div></div>
                <div className="text-right"><div className="font-mono text-sm">{view.quote.number}</div><Badge className="mt-1">{view.quote.status}</Badge></div>
              </div>
              <div className="text-sm"><b>Cliente:</b> {view.customer?.name} — {view.customer?.company}</div>
              <table className="w-full text-sm border">
                <thead className="bg-slate-100"><tr><th className="text-left p-2">Produto</th><th>Qtd</th><th>Preço</th><th>Total</th></tr></thead>
                <tbody>{view.quote.items.map((i,ix)=>(<tr key={ix} className="border-t"><td className="p-2">{i.product}</td><td className="text-center">{i.quantity}</td><td className="text-center">{BRL(i.unit_price)}</td><td className="text-center">{BRL(i.quantity*i.unit_price)}</td></tr>))}</tbody>
              </table>
              <div className="text-right space-y-1 text-sm"><div>Subtotal: {BRL(view.quote.subtotal)}</div><div>Desconto: -{BRL(view.quote.discount)}</div><div className="text-lg font-bold">Total: {BRL(view.quote.total)}</div></div>
              {view.quote.notes && <div className="text-xs text-slate-600 border-t pt-3"><b>Observações:</b> {view.quote.notes}</div>}
              <div className="text-xs text-slate-500 border-t pt-3">Validade: {view.quote.validity_days} dias — expira em {new Date(view.quote.expires_at).toLocaleDateString("pt-BR")}</div>
              <Button onClick={()=>window.print()} className="w-full bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E] font-semibold" data-testid="quote-print">Imprimir</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
